import { useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { ArrowRight, Mail } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { Button, Field, Input } from "../../components/ui"
import { AuthLayout, OAuthButtons } from "./AuthLayout"
import { CodeStep } from "./CodeStep"

/**
 * Step one of two.
 *
 * Email and password here never return a session - they create a challenge and
 * send a six-digit code. The session only exists after the code is verified,
 * which is what makes a leaked password insufficient on its own.
 */
export function SignInPage() {
	const { config, adoptSession, isSignedIn } = useApp()
	const navigate = useNavigate()
	const [params] = useSearchParams()

	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(params.get("error") || null)
	const [challenge, setChallenge] = useState(null)

	const next = "/app"

	useEffect(() => {
		if (isSignedIn) navigate(next, { replace: true })
	}, [isSignedIn, navigate, next])

	async function submit(event) {
		event.preventDefault()
		setBusy(true)
		setError(null)
		try {
			const started = await api.signIn({ email: email.trim(), password })
			setChallenge(started)
		} catch (cause) {
			setError(cause.message)
		} finally {
			setBusy(false)
		}
	}

	if (challenge) {
		return (
			<CodeStep
				challenge={challenge}
				email={email.trim()}
				onBack={() => setChallenge(null)}
				onVerified={async (session) => {
					await adoptSession(session.token, session.user)
					navigate(next, { replace: true })
				}}
			/>
		)
	}

	return (
		<AuthLayout
			title="Sign in"
			description="We will email a six-digit code to confirm it is you."
			footer={
				<>
					No account yet?{" "}
					<Link
						to="/register"
						className="font-medium text-brand-700 hover:underline"
					>
						Create one
					</Link>
				</>
			}
		>
			<div>
				<form onSubmit={submit} className="space-y-3.5">
					<Field label="Work email">
						<Input
							type="email"
							autoComplete="email"
							value={email}
							onChange={(event) => setEmail(event.target.value)}
							placeholder="you@company.com"
							required
							autoFocus
						/>
					</Field>
					<div>
						<Field label="Password" error={error}>
							<Input
								type="password"
								autoComplete="current-password"
								value={password}
								onChange={(event) => setPassword(event.target.value)}
								required
							/>
						</Field>
						{/* Outside the Field: an anchor inside a <label> would also
						    focus the input when clicked. */}
						<div className="mt-1.5 text-right">
							<Link
								to={`/forgot-password${
									email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ""
								}`}
								className="text-2xs text-brand-700 hover:underline"
							>
								Forgot password?
							</Link>
						</div>
					</div>
					<Button
						type="submit"
						variant="primary"
						size="lg"
						className="w-full"
						icon={Mail}
						iconRight={ArrowRight}
						busy={busy}
						disabled={busy || !email.trim() || !password}
					>
						Email me a code
					</Button>
				</form>

				<OAuthButtons providers={config?.oauth_providers} next={next} disabled={busy} />
			</div>
		</AuthLayout>
	)
}
